import React, { useCallback, useEffect, useState } from "react";
import { Heading, Rating } from "@lomarengas/design-system";
import Page from "./../../types/Page";
import Layout from "../../components/Layout";
import {
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Nav,
  Row,
  Spinner,
  Tab,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import DatePicker from "react-datepicker";
import { fi } from "date-fns/locale";
import CabinCard from "../../components/CabinCard";
import { useAuthUser } from "../../features/auth/authHooks";
import NewFeedBacks from "./NewFeedBacks";
import StrongAuthBanner from "../../components/StrongAuthBanner";
import InfoWebCoreApi from "../../common/infoweb/infoWebCoreApi";
import WidgetArea from "../../components/Widgets/WidgetArea";
import { PageData } from "../../types/InfoWebTypes";
import ReactDayPickerWrapper from "../../components/DayPicker/ReactDayPickerWrapper";
import { DateRange } from "react-day-picker";
import {
  constructCalendarReservedDatesDummy,
  convertJSDateToDotNetDateTimeDayEnd,
  convertJSDateToDotNetDateTimeDayStart,
  convertJSDateToDotNetDateTimeWithTime,
  formattedAmountWithEuroCurrency,
} from "../../common/misc";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { getUserClaimsAsync } from "../../features/auth/authSlice";
import {
  AccommodationListItem,
  AccommodationQueryFilters,
  AvailableAccommodationsFilter,
  clearAvailableAccommodationItemsHomePage,
  getAccommodationsAsync,
  getAvailableAccommodationsAsync,
  getAvailableAccommodationsHomePageAsync,
  selectAccommodationItems,
  selectAccommodationsLoadingStatus,
  selectAvailableAccommodationItems,
  selectAvailableAccommodationItemsHomePage,
  selectAvailableAccommodationsLoadingStatus,
  selectAvailableAccommodationsLoadingStatusHomePage,
} from "../AccommodationsPage/accommodationsSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  InitialAllAccommodationsPaginationParameters,
  InitialPaginationParameters,
} from "../../types/pagination.types";
import { LOADING_STATUS } from "../../common/constants";
import { selectInputErrors } from "../../features/errors/errorsSlice";
import {
  BookingItem,
  BookingsRequest,
  getAccommodationsBookingsAsync,
} from "../BookingsPage/bookingsSlice";
// import AccommodationBookings from "../BookingsPage/AccommodationBookings";
import FrontPageAccommodationBookings from "../BookingsPage/FrontPageAccommodationBookings";
import BookingRowsTable from "../BookingsPage/BookingRowsTable";
import BookingModalWrapper from "../BookingsPage/BookingModalWrapper";
import { format, addDays } from "date-fns";
import BookingsModalWrapper from "../BookingsPage/BookingsModalWrapper";
import LocalizedDatePicker from "../../components/DatePicker/LocalizedDatePicker";
import BookingsStartingInSevenDays from "../BookingsPage/BookingsStartingInSevenDays";
import AccommodationBookings from "../BookingsPage/AccommodationBookings";
import BookingsCreatedInSevenDays from "../BookingsPage/BookingsCreatedInSevenDays";

const HomePageType: Page = {
  ActiveTab: "Home",
  DisplayName: "Etusivu",
  LinkUrl: "/",
  Path: "/",
  RouteName: "home",
};

const HomePage = () => {
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();
  const { isOwnerUser } = useAuthUser();
  const { isLoggedIn } = useAuthUser();
  const { loginFirstName, loginLastName } = useAuthUser();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Ensure correct account is selected before loading homepage data
  const [accountCheckDone, setAccountCheckDone] = useState(false);

  useEffect(() => {
    const ensureAccountSelected = async () => {
      try {
        if (!isLoggedIn) return; // wait until logged in
        // Ask backend which accounts are tied to this email
        const accounts: any[] = await apiService.get(
          API_ENDPOINTS.IDENTITY.GET_USER_ACCOUNTS,
        );
        if (!Array.isArray(accounts)) {
          setAccountCheckDone(true);
          return;
        }
        if (accounts.length > 1) {
          // For multiple accounts, require selection once per login session
          const selectionDone =
            localStorage.getItem("accountSelectionDone") === "true";
          if (selectionDone) {
            setAccountCheckDone(true);
            return;
          }
          // Otherwise redirect to account selection page
          if (window.location.pathname !== "/select-account") {
            window.location.replace("/select-account");
            return;
          }
        } else if (accounts.length === 1) {
          // If already done this login session, just proceed
          const selectionDone =
            localStorage.getItem("accountSelectionDone") === "true";
          if (selectionDone) {
            setAccountCheckDone(true);
            return;
          }
          // Auto-select the only account, then refresh claims
          const first = accounts[0];
          const id = first?.id ?? first?.accountId;
          if (id) {
            try {
              await apiService.post(
                API_ENDPOINTS.IDENTITY.SELECT_LOGIN_ACCOUNT,
                JSON.stringify(id),
              );
              await dispatch(getUserClaimsAsync()).unwrap();
              localStorage.setItem("accountSelectionDone", "true");
            } catch (e) {
              // ignore and continue
            }
          }
          setAccountCheckDone(true);
        } else {
          // No accounts; allow page to load (may show no rights)
          setAccountCheckDone(true);
        }
      } catch (e) {
        setAccountCheckDone(true);
      }
    };
    ensureAccountSelected();
  }, [dispatch, isLoggedIn]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAccommodationTab = searchParams.get(
    "selectedAccommodationForTabs",
  );
  const selectedDateForTabCalendar = searchParams.get(
    "selectedDateForTabCalendar",
  );
  const [
    frontPageAccommodationBookingsRefreshTrigger,
    setFrontPageAccommodationBookingsRefreshTrigger,
  ] = useState(0); // A changing number will force re-fetch
  if (!isOwnerUser) {
    //redirect to caretaker homepage, which changed in 29.10.2025 to bookings page
    window.location.href = "/bookings";
  }

  //console.log(useAuthUser());
  const inputErrors = useAppSelector(selectInputErrors);
  const accommodations = useAppSelector(selectAccommodationItems);
  const sortedAccommodations = [...accommodations].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );

  const availableAccommodations = useAppSelector(
    selectAvailableAccommodationItemsHomePage,
  );

  const sortedAvailableAccommodations = [...availableAccommodations].sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  const availableAccommodationsLoadingStatusHomePage = useAppSelector(
    selectAvailableAccommodationsLoadingStatusHomePage,
  );

  const [selectedAvailableAccommodations, setSelectedAvailableAccommodations] =
    useState<string[]>([]);

  const [selectedTab, setSelectedTab] = useState<number | null>(
    selectedAccommodationTab != null ? +selectedAccommodationTab : 0,
  );
  const [loadedTabs, setLoadedTabs] = useState<{ [key: number]: boolean }>({
    [selectedTab ?? 0]: true, // Use dynamic key
  }); // Initialize first tab as loaded
  const [updatedBookings, setUpdatedBookings] = useState<BookingItem[]>(null); // Store updated bookings
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  // Determine active accommodation based on selected tab
  const activeAccommodation =
    sortedAccommodations[(selectedTab ?? 0) as number];
  const [fetchedAvailabilities, setFetchedAvailabilities] = useState(false);
  // Get today's date
  const today = new Date();

  // Create a date object for tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: today,
      to: tomorrow,
    });
  const [lastFetchedAvailabilitiesDates, setLastFetchedAvailabilitiesDates] =
    useState<DateRange>({
      from: today,
      to: tomorrow,
    });
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(tomorrow);
  const demoDate1 = new Date();
  const demoMonth = demoDate1.getMonth();
  const demoYear = demoDate1.getFullYear();
  const demoDate2 = new Date(demoYear, demoMonth, demoDate1.getDate() + 1);
  const excludedDays = [
    new Date(`${demoYear}/${demoMonth + 1}/1`),
    new Date(`${demoYear}/${demoMonth + 1}/20`),
    new Date(`${demoYear}/${demoMonth + 1}/22`),
  ];

  const [pageData, setPageData] = useState<PageData>();
  const [
    accommodationDropdownSelectedAccommodation,
    setAccommodationDropdownSelectedAccommodation,
  ] = useState<AccommodationListItem>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      var pd = await InfoWebCoreApi.GetPageByPathAsync("fi/omaliittyma");

      if (pd != null) {
        setPageData(pd);
      }
    };

    fetchData().catch(console.error);
  }, []);
  const [filtering, setFiltering] = useState<AccommodationQueryFilters>({
    PageNumber: InitialAllAccommodationsPaginationParameters.PageNumber,
    PageSize: InitialAllAccommodationsPaginationParameters.PageSize,
    filterCriteria: {
      showDeleted: false,
      searchString: undefined,
      accommodationNumber: undefined,
      novelty: undefined,
      showInWebShop: undefined,
      showInOwnerPage: undefined,
      accommodationGroupId: undefined,
      accountId: ownerAccountId,
    },
  });
  const initAccommodationsFetch = useCallback(() => {
    dispatch(getAccommodationsAsync(filtering));
  }, [dispatch, ownerAccountId]);
  useEffect(() => {
    initAccommodationsFetch();
  }, [initAccommodationsFetch, ownerAccountId]);

  // This function will be passed to the child component to handle updates
  const handleBookingsUpdate = (newBookings: any) => {
    setUpdatedBookings(newBookings);
    // You can also handle further logic here, like dispatching to Redux
    // dispatch(updateBookings({ accommodationId: selectedAccommodation.id, bookings: newBookings }));
  };
  const accommodationDropdownChanged = (e: any) => {
    setFetchedAvailabilities(false);
    if (e.target.value != "0") {
      let selectedAccommodation = sortedAccommodations.find(
        (accommodation) => accommodation.id == e.target.value,
      );
      setAccommodationDropdownSelectedAccommodation(selectedAccommodation);
    } else {
      setAccommodationDropdownSelectedAccommodation(undefined);
    }
  };
  const setAccommodationDropdownDateRange = (
    startDateSelected: Date,
    endDateSelected: Date,
  ) => {
    let selectedRange = dayPickerSelectedRange;
    if (startDateSelected != undefined) {
      selectedRange.from = startDateSelected;
      setStartDate(startDateSelected);
      // Move enddate, if selected startdate is greater than endDate
      if (endDate < startDateSelected) {
        const newEndDate = new Date(startDateSelected);
        newEndDate.setDate(newEndDate.getDate() + 1);
        selectedRange.to = newEndDate;
        setEndDate(newEndDate);
      }
    }
    if (endDateSelected != undefined) {
      selectedRange.to = endDateSelected;
      setEndDate(endDateSelected);
    }
    setDayPickerSelectedRange(selectedRange);
  };
  const selectTab = useCallback(
    (index) => {
      setSelectedTab(index);
      // Mark the tab as loaded to avoid re-fetching data on re-selection
      if (!loadedTabs[index]) {
        setLoadedTabs((prev) => ({ ...prev, [index]: true }));
      }
      searchParams.set("selectedAccommodationForTabs", index);
      searchParams.delete("selectedDateForTabCalendar");
      setSearchParams(searchParams);
    },
    [loadedTabs],
  );

  // Block rendering until account selection is handled
  if (!accountCheckDone) {
    return (
      <div className={HomePageType.RouteName}>
        <Layout CurrentPage={HomePageType}>
          <div className="d-flex gap-16 align-items-center">
            <Spinner animation="border" />
            <span>Tarkistetaan käyttäjätiliä…</span>
          </div>
        </Layout>
      </div>
    );
  }

  if (
    accommodationsLoadingStatus == LOADING_STATUS.LOADING ||
    accommodations == null
  ) {
    return (
      <div className={HomePageType.RouteName}>
        <Layout CurrentPage={HomePageType}>
          <div className="d-flex gap-16 align-items-center">
            <Spinner animation="border" />
            <span>Ladataan kohteita...</span>
          </div>
        </Layout>
      </div>
    );
  }
  /*
        if (accommodations.length == 0) {
          //no accommodations
          //console.log("no accommodation");
          return (
            <div
              className="d-flex w-100 justify-content-center align-items-center"
              style={{ height: "100vh" }}
            >
              <div className="card shadow">
                <div className="card-body text-center">
                  <h1 className="h4 mb-16 d-flex align-items-center justify-content-center">
                    <span className="text-danger bi bi-exclamation-triangle me-16 fs-1"></span>
                    Ei kohteita
                  </h1>
                  <p>Sinulla ei ole oikeuksia tähän palveluun.</p>
                  <a href="{logoutUrl}">Takaisin kirjautumiseen</a>
                </div>
              </div>
            </div>
          );
        }
         */
  /*
            const selectTab = (accommodation, accommodationCounter) => {
              setSelectedAccommodation(accommodation);
              setSelectedAccommodationTabKey(accommodationCounter);
              console.log(accommodationCounter);
            };
        
             */
  const fetchAvailableAccommodations = () => {
    // Clear selected accommodations for every search
    setSelectedAvailableAccommodations([]);
    setFetchedAvailabilities(true);
    let availableAccommodationsFilter: AvailableAccommodationsFilter =
      new (class implements AvailableAccommodationsFilter {
        accommodationId: string;
        endDate: string;
        startDate: string;
      })();
    availableAccommodationsFilter.startDate =
      convertJSDateToDotNetDateTimeWithTime(dayPickerSelectedRange.from);
    availableAccommodationsFilter.endDate =
      convertJSDateToDotNetDateTimeWithTime(dayPickerSelectedRange.to);
    if (accommodationDropdownSelectedAccommodation != null) {
      availableAccommodationsFilter.accommodationId =
        accommodationDropdownSelectedAccommodation.id;
    }
    setLastFetchedAvailabilitiesDates({
      from: new Date(dayPickerSelectedRange.from),
      to: new Date(dayPickerSelectedRange.to),
    });
    dispatch(
      getAvailableAccommodationsHomePageAsync(availableAccommodationsFilter),
    );
  };
  const handleBookingModalClose = () => {
    setFrontPageAccommodationBookingsRefreshTrigger((prev) => prev + 1); // Increment to trigger re-fetch
    setBookingModalOpen(false);
  };
  const afterBookingsModalCompleted = () => {
    fetchAvailableAccommodations();
  };
  const clearSearchResults = () => {
    setFetchedAvailabilities(false);
    setSelectedAvailableAccommodations([]);
    dispatch(clearAvailableAccommodationItemsHomePage());
  };
  const allAccommodationIds = (accommodations || []).map((a) => a.id);
  return (
    <div className={HomePageType.RouteName}>
      <Layout CurrentPage={HomePageType}>
        <StrongAuthBanner />
        <div className="text-nowrap">
          <Heading CssClass="mt-0 mb-16" Level={4} Title="Tee omavaraus" />
        </div>
        <Row className="gy-16">
          <Col xl="4">
            <Form.Label htmlFor="selectCabin">Kohde</Form.Label>
            <Form.Select
              aria-label="Kohde"
              id="selectCabin"
              onChange={(e) => accommodationDropdownChanged(e)}
            >
              {sortedAccommodations.length > 1 && (
                <option value={0}>Näytä kaikki kohteet</option>
              )}
              {sortedAccommodations.map((accommodation) => {
                return (
                  <option key={accommodation.id} value={accommodation.id}>
                    {accommodation.accommodationNumber}, {accommodation.name}
                  </option>
                );
              })}
            </Form.Select>
          </Col>
          <Col md="7" xl="5">
            <Form.Label htmlFor="selectStart">Valitse ajankohta</Form.Label>
            <div className="d-flex  gap-8 align-items-center">
              <div className="w-100 position-relative">
                <LocalizedDatePicker
                  selected={startDate}
                  onChange={(date) =>
                    setAccommodationDropdownDateRange(date, undefined)
                  }
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  minDate={today}
                  className="form-control"
                  dateFormat={"dd.MM.yyyy"}
                  locale={fi}
                />
                <Button
                  variant="link"
                  className="px-8 px-sm-16 position-absolute end-0 top-0 pe-none"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event"></span>
                </Button>
              </div>
              <span className="bi bi-arrow-right"></span>
              <div className="w-100 position-relative">
                <LocalizedDatePicker
                  selected={endDate}
                  onChange={(date) =>
                    setAccommodationDropdownDateRange(undefined, date)
                  }
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  //Earliest end is the day after start
                  minDate={startDate ? addDays(startDate, 1) : undefined}
                  className="form-control"
                  dateFormat={"dd.MM.yyyy"}
                />
                <Button
                  variant="link"
                  className="px-8 px-sm-16 position-absolute end-0 top-0 pe-none"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event"></span>
                </Button>
              </div>
            </div>
          </Col>
          <Col md="5" xl="3" className="d-flex align-items-end">
            <Button
              onClick={() => fetchAvailableAccommodations()}
              variant="primary"
              className="mt-auto w-100 text-nowrap"
            >
              Hae saatavuus & hinta
            </Button>
          </Col>
        </Row>
        {availableAccommodationsLoadingStatusHomePage !=
          LOADING_STATUS.IDLE && (
          <div className="d-flex gap-16 align-items-center mt-24">
            <Spinner animation="border" />
            <span> Ladataan saatavilla olevia kohteita...</span>
          </div>
        )}
        {availableAccommodationsLoadingStatusHomePage == LOADING_STATUS.IDLE &&
          sortedAvailableAccommodations != null &&
          sortedAvailableAccommodations.length > 0 && (
            <>
              <span className="form-label d-block  mt-24">
                Vapaat kohteet ajalle:{" "}
                {format(lastFetchedAvailabilitiesDates.from, "dd.MM.yyyy")} -{" "}
                {format(lastFetchedAvailabilitiesDates.to, "dd.MM.yyyy")}
              </span>
            </>
          )}
        {availableAccommodationsLoadingStatusHomePage == LOADING_STATUS.IDLE &&
          fetchedAvailabilities &&
          sortedAvailableAccommodations != null &&
          sortedAvailableAccommodations.length == 0 && (
            <div className="alert alert-primary mt-24">
              {accommodationDropdownSelectedAccommodation == null
                ? `Ei vapaita kohteita ajalle: ${format(lastFetchedAvailabilitiesDates.from, "dd.MM.yyyy")} - ${format(lastFetchedAvailabilitiesDates.to, "dd.MM.yyyy")}`
                : `Kohde ${accommodationDropdownSelectedAccommodation.name} ei vapaana ajalle: ${format(lastFetchedAvailabilitiesDates.from, "dd.MM.yyyy")} - ${format(lastFetchedAvailabilitiesDates.to, "dd.MM.yyyy")}`}
            </div>
          )}
        {sortedAvailableAccommodations.length > 0 && (
          <div className="list-group bg-white rounded-bottom-0">
            {sortedAvailableAccommodations.map((accommodation) => (
              <label
                key={`availableAccommodation${accommodation.accommodationId}`}
                htmlFor={`checkbox-${accommodation.accommodationId}`}
                className="list-group-item list-group-item-action d-flex align-items-center gap-16"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  id={`checkbox-${accommodation.accommodationId}`}
                  checked={selectedAvailableAccommodations.includes(
                    accommodation.accommodationId,
                  )}
                  onChange={() => {
                    setSelectedAvailableAccommodations((prevSelected) =>
                      prevSelected.includes(accommodation.accommodationId)
                        ? prevSelected.filter(
                            (id) => id !== accommodation.accommodationId,
                          )
                        : [...prevSelected, accommodation.accommodationId],
                    );
                  }}
                />

                {accommodation.thumbImageUrl && (
                  <img
                    src={accommodation.thumbImageUrl}
                    alt={accommodation.name}
                    className="rounded rounded-1"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                    }}
                  />
                )}

                <span>
                  {accommodation.accommodationNumber}, {accommodation.name},{" "}
                  <span className="text-muted d-block">
                    {formattedAmountWithEuroCurrency(accommodation.price)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        {sortedAvailableAccommodations != null &&
          sortedAvailableAccommodations.length > 0 && (
            <>
              <Button
                disabled={selectedAvailableAccommodations.length === 0}
                onClick={() => setBookingModalOpen(true)}
                variant="primary"
                className="mt-auto w-100 rounded-top-0"
              >
                Varaa
              </Button>
              <Button
                onClick={() => clearSearchResults()}
                variant="primary"
                className="mx-auto d-block btn mt-16 btn-link"
              >
                Tyhjennä hakutulos
              </Button>
            </>
          )}
        {/*}
        <Heading Level={4} Title="Kohteesi" />

        <Tab.Container
          id="left-tabs-example"
          activeKey={"tab" + (selectedTab + 1)} // Päivitetään aktiivinen tab
        >
          <select
            className="form-select"
            value={selectedTab}
            onChange={(e) => selectTab(Number(e.target.value))} // Päivitetään selectedTab
          >
            {sortedAccommodations.map((accommodation, index) => (
              <option key={`option${accommodation.id}`} value={index}>
                {accommodation.accommodationNumber}, {accommodation.name}
              </option>
            ))}
          </select>

          <Tab.Content>
            {sortedAccommodations.map((accommodation, index) => {
              const isActive = selectedTab === index; // Tarkistetaan, onko tab aktiivinen
              const isLoaded = !!loadedTabs[index];
              return (
                <Tab.Pane
                  key={`tab${accommodation.id}`}
                  eventKey={`tab${index + 1}`}
                >
                  {isActive && (
                    <div className="d-grid gap-32">
                      <Row className="g-16">
                        <Col>
                          {isLoaded ? (
                            <CabinCard
                              Url={"accommodations/modify/" + accommodation.id}
                              Horizontal={isMobile}
                              className="h-100"
                              ImgSrc={accommodation.imageUrl}
                              Title={`${accommodation.accommodationNumber}, ${accommodation.name}`}
                              Desc={`${accommodation.beds} + ${accommodation.extraBeds} henkilöä`}
                              Location={
                                (accommodation.municipality != null &&
                                accommodation.municipality.length > 0
                                  ? accommodation.municipality
                                  : "") +
                                (accommodation.region != null &&
                                accommodation.region.length > 0
                                  ? ", " + accommodation.region
                                  : "") +
                                (accommodation.skiResort != null &&
                                accommodation.skiResort.length > 0
                                  ? ", " + accommodation.skiResort
                                  : "")
                              }
                            />
                          ) : (
                            <Spinner animation="border" />
                          )}
                        </Col>
                        <Col lg={7}>
                          <div className="react-datepicker-inline">
                            {isLoaded ? (
                              <FrontPageAccommodationBookings
                                key={accommodation.accommodationNumber}
                                accommodation={accommodation}
                                onBookingsUpdate={handleBookingsUpdate}
                                refreshTrigger={
                                  frontPageAccommodationBookingsRefreshTrigger
                                }
                              />
                            ) : (
                              <Spinner animation="border" />
                            )}
                          </div>
                        </Col>
                      </Row>
                      <div>
                        <h5>
                          <span className="d-inline-block lh-1 bg-primary rounded-circle p-8 fs-6 me-8">
                            <span className="bi bi-calendar-week"></span>
                          </span>{" "}
                          Tulevat varaukset
                        </h5>

                        {isLoaded ? (
                          <BookingRowsTable
                            bookings={updatedBookings}
                            accommodationId={accommodation.id}
                            clearAllPastBookings={true}
                          />
                        ) : (
                          <Spinner animation="border" />
                        )}
                      </div>
                      {isLoaded ? (
                        <NewFeedBacks accommodationId={accommodation.id} />
                      ) : (
                        <Spinner animation="border" />
                      )}
                    </div>
                  )}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
        {*/}
        {selectedAvailableAccommodations != undefined && (
          <BookingsModalWrapper
            selectedAvailableAccommodations={selectedAvailableAccommodations}
            availableAccommodations={sortedAvailableAccommodations}
            showModal={bookingModalOpen}
            selectedDateRange={dayPickerSelectedRange}
            setParentShowBookingModal={handleBookingModalClose}
            callParentInitFetch={afterBookingsModalCompleted}
            callParentResetSearch={clearSearchResults}
            callParentResetSelectedAccommodations={() =>
              setSelectedAvailableAccommodations([])
            }
          />
        )}

        <hr className="mt-24" />
        <div className="mb-24 mt-24">
          <BookingsStartingInSevenDays></BookingsStartingInSevenDays>
        </div>
        <hr />
        <div className="mb-24 mt-24">
          <BookingsCreatedInSevenDays></BookingsCreatedInSevenDays>
        </div>
        <hr />
        <h2 className="h4 mb-16">Kohteet</h2>
        <div className="d-grid gap-16 gap-lg-32">
          <>
            {accommodations != null &&
              accommodations.map((accommodation, index) => {
                return (
                  <AccommodationBookings
                    key={accommodation.accommodationNumber}
                    accommodation={accommodation}
                    allAccommodationIds={allAccommodationIds}
                    useBatchCalendarFetch={true}
                    isBatchCalendarFetchLeader={index === 0}
                  />
                );
              })}
          </>
        </div>
        <WidgetArea pageData={pageData} />
      </Layout>
    </div>
  );
};

export default HomePage;
